<template>
  <div class="container">
    <breadcrumb level="student"></breadcrumb>
    <div class="edit-student">
      <b-container fluid class="mx-auto">
        <h1 class="title">{{ title }}</h1>
        <h3 class="title">{{ getFirstName }} {{ getLastName }}</h3>
        <div id="alerts">
          <b-alert
            :show="dismissCountDown"
            dismissible
            variant="success"
            @dismissed="dismissCountDown = 0"
            @dismiss-count-down="countDownChanged"
          >You have successfully edited a student!</b-alert>
          <b-alert :show="showError" dismissible variant="warning">
            Something went wrong. Please ensure you entered at least a first
            name, last name, sex and current teacher.
          </b-alert>
        </div>
        <b-row>
          <b-col>
            <active-input plural="students" singular="student"></active-input>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <student-basic singular="student" plural="students"></student-basic>
          </b-col>
          <b-col>
            <geo-info plural="students" singular="student" :coords="false"></geo-info>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <duplicate-input plural="students" singular="student"></duplicate-input>
          </b-col>
        </b-row>
        <b-card no-body>
          <b-tabs card>
            <b-tab title="Data Consent">
              <h4 class="sub-title">Data Consent</h4>
              <data-consent id="data-consent" type="student"></data-consent>
            </b-tab>
            <b-tab title="Attendance" active>
              <div class="attendance">
                <h4 class="sub-title">Attendance</h4>
                <student-attendance
                  :first_name="getFirstName"
                  :last_name="getLastName"
                  :school="getCurrentSchool"
                ></student-attendance>
              </div>
            </b-tab>
            <b-tab title="Feeding">
              <div class="school-feeding">
                <h4 class="sub-title">School Feeding</h4>
                <student-feeding
                  :first_name="getFirstName"
                  :last_name="getLastName"
                  :school="getCurrentSchool"
                ></student-feeding>
              </div>
            </b-tab>
            <b-tab title="Student Parent/Guardian">
              <h4 class="sub-title">Student Parents/Guardians</h4>
              <student-parents></student-parents>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-container>
    </div>
    <br />
    <confirm-submit v-if="isAdmin"></confirm-submit>
    <b-button
      v-if="isAdmin"
      :size="'lg'"
      :variant="'primary'"
      class="app_school_btn mb-4"
      @click.prevent="updateStudent"
      :disabled="isDisabled"
    >{{ title }}</b-button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TeacherService from "../services/TeacherService";
import ConfirmSubmit from "../components/ConfirmSubmit";
import StudentBasic from "../components/StudentBasic";
import GeoInfo from "../components/GeoInfo";
import ActiveInput from "@/components/ActiveInput";
import DuplicateInput from "@/components/DuplicateInput";
import StudentAttendance from "../components/StudentAttendance";
import StudentFeeding from "../components/StudentFeeding";
import StudentDropout from "../components/StudentDropout";
import StudentTracking from "../components/StudentTracking";
import StudentParents from "../components/StudentParents";
import Breadcrumb from "@/components/Breadcrumb";
import DataConsent from "@/components/DataConsent";

export default {
  name: "EditStudent",
  components: {
    StudentBasic,
    GeoInfo,
    ActiveInput,
    DuplicateInput,
    StudentAttendance,
    StudentDropout,
    StudentFeeding,
    StudentTracking,
    ConfirmSubmit,
    StudentParents,
    Breadcrumb,
    DataConsent
  },
  data() {
    return {
      itemsPerRow: 20,
      addOrEdit: this.$route.path.includes("/add"),
      showError: false,
      selectCurrent: false,
      selectPrevious: false,
      dismissSecs: 3,
      dismissCountDown: 0,
      dismissParentCountDown: 0,
      isDisabled: false,
      allTeachers: [],
      allTrainers: this.$store.state.trainers.trainers
    };
  },
  computed: {
    ...mapGetters({
      getFirstName: "students/getFirstName",
      getLastName: "students/getLastName",
      getAllTrainers: "trainers/getTrainers",
      getCurrentSchool: "students/getCurrentSchool",
      getCurrentSchoolId: "students/getCurrentSchoolId",
      getCurrentTeacher: "students/getCurrentTeacher",
      getCurrentTeacherId: "students/getCurrentTeacherId",
      isAdmin: "auth/isAdmin"
    }),
    title() {
      if (this.$route.path.includes("/add")) {
        return "Add Student";
      }
      return "Edit Student";
    },
    schoolsTeachers: {
      get() {
        return this.$store.state.students.schoolsTeachers;
      }
    }
  },
  methods: {
    ...mapActions({
      GET_STUDENT: "students/GET_STUDENT",
      GET_STUDENT_SCHOOL_TEACHER: "students/GET_STUDENT_SCHOOL_TEACHER",
      UPDATE_STUDENT: "students/UPDATE_STUDENT",
      RESET_STUDENTS_STATE: "students/RESET_STUDENTS_STATE",
      RESET_ATTENDANCE_STATE: "attendance/RESET_ATTENDANCE_STATE",
      GET_TRAINERS: "trainers/GET_TRAINERS",
      RESET_TRAINERS_STATE: "trainers/RESET_TRAINERS_STATE",
      RESET_TRACKINGS_STATE: "trackings/RESET_TRACKINGS_STATE",
      UPDATE_STUDENT_FEEDINGS: "feedings/UPDATE_STUDENT_FEEDING_RECORDS",
      RESET_FEEDINGS_STATE: "feedings/RESET_FEEDINGS_STATE"
    }),
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    flashSuccess() {
      this.dismissCountDown = this.dismissSecs;
    },
    flashError() {
      this.showError = !this.showError;
    },
    async updateStudent() {
      if (!this.$store.state.confirmed) {
        alert("Please confirm the information above is correct");
        return;
      }
      if (this.getFirstName.length === 0 || !this.getLastName.length === 0) {
        window.scrollTo(0, 0);
        this.flashError();
        return;
      }
      this.isDisabled = true;
      window.scrollTo(0, 0);
      await this.UPDATE_STUDENT();
      await this.flashSuccess();
      await this.$store.commit("resetConfirmed");
      await this.RESET_STUDENTS_STATE();
      await this.RESET_ATTENDANCE_STATE();
      await this.RESET_FEEDINGS_STATE();
      await this.RESET_TRAINERS_STATE();
      await this.RESET_TRACKINGS_STATE();
      setTimeout(() => this.$router.push({ name: "StudentsList" }), 1500);
    },
    async getTeachers() {
      const res = await TeacherService.fetchTeacherNames();
      this.allTeachers = res.data;
    }
  },
  created() {
    this.GET_STUDENT();
    this.GET_STUDENT_SCHOOL_TEACHER();
    this.GET_TRAINERS();
  }
};
</script>

<style scoped>
.container {
  align-content: center;
  width: 100%;
}
.form div {
  margin: 20px;
}
.app_school_btn {
  background: #4d7ef7;
  color: #fff;
  padding: 10px 80px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  width: 520px;
  border: none;
  cursor: pointer;
}
label {
  margin-top: 2%;
  margin-bottom: -0.5%;
}
label.left {
  float: left;
}
div.col {
  margin: 1%;
  padding: 1%;
  border: 1px solid gray;
}
div#assigned-schools {
  float: left;
}
.custom-control-label::before {
  font-size: 10px;
}
#previous-teachers {
  list-style-type: none;
  display: inline;
}

.sub-sub-title {
  float: left;
}
div.col.ignore-outline {
  border: 0px solid white;
}
div.col.box {
  max-height: 250px;
  overflow-y: auto;
}
</style>
